import React from 'react';
import i18n from '../../../../../i18n';
import { fmt, METRICS } from '../../../data/metrics';
import type { DrillGrid, DrillGridEntry } from '../../../types';
import { DrillTile, type DrillTileSpan } from './DrillTile';

/** 0…1 heat per value across the cells with enough reservations behind them. */
function heatScale(grid: DrillGrid): (v: number | null) => number {
  const values = grid.cells
    .filter(c => !c.muted && c.value != null)
    .map(c => c.value as number);
  if (!values.length) return () => 0;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return v => {
    if (v == null) return 0;
    if (hi === lo) return 0.5;
    return Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
  };
}

function deltaText(e: DrillGridEntry): string | null {
  if (e.delta == null) return null;
  return `${e.delta >= 0 ? '+' : ''}${e.delta}${e.deltaUnit ?? '%'}`;
}

function GridValue({
  entry,
  fmtVal,
  goodHigh,
}: {
  entry: DrillGridEntry;
  fmtVal: (v: number) => string;
  goodHigh: boolean;
}) {
  const delta = entry.muted ? null : deltaText(entry);
  const good =
    entry.delta == null ? undefined : goodHigh ? entry.delta >= 0 : entry.delta <= 0;
  return (
    <>
      <span className="ddrill-heat-val">
        {entry.value == null ? '–' : fmtVal(entry.value)}
      </span>
      {delta ? (
        <span
          className="ddrill-heat-delta"
          style={{ color: good ? 'var(--st-steady)' : 'var(--st-alert)' }}
        >
          {delta}
        </span>
      ) : null}
    </>
  );
}

export function DrillGridSection({
  grid,
  fmtVal,
  compareLabel,
  span = 8,
  selectedDows,
  onToggleDow,
}: {
  grid: DrillGrid;
  fmtVal: (v: number) => string;
  compareLabel?: string;
  span?: DrillTileSpan;
  /** Weekdays narrowing the hour profile (empty = all). */
  selectedDows?: number[];
  /** Present when an hour profile sits next to the grid. */
  onToggleDow?: (dow: number) => void;
}) {
  const metric = METRICS[grid.metricKey];
  const short = metric?.short ?? grid.metricKey;
  const goodHigh = metric?.better !== 'low';
  const heat = heatScale(grid);
  const dayLabel = new Map(grid.rows.map(r => [r.dow, r.label]));
  const colLabel = new Map(grid.columns.map(c => [c.key, c.label]));
  const cellAt = new Map(grid.cells.map(c => [`${c.dow}|${c.col}`, c]));
  const rowTotal = new Map(grid.rowTotals.map(r => [r.dow, r]));
  const colTotal = new Map(grid.colTotals.map(c => [c.col, c]));
  const peakKey = grid.peak ? `${grid.peak.dow}|${grid.peak.col}` : null;
  const peakCell = peakKey ? cellAt.get(peakKey) : undefined;

  const cellTitle = (
    day: string,
    shift: string,
    e: DrillGridEntry,
  ): string => {
    const parts = [
      i18n.t('performance:drillUi.gridCellTitle', {
        day,
        shift,
        value: e.value == null ? '–' : fmtVal(e.value),
        n: fmt.num(e.n),
      }),
    ];
    if (grid.comparable && compareLabel && e.compareValue != null) {
      parts.push(
        i18n.t('performance:drillUi.gridCellCompare', {
          label: compareLabel,
          value: fmtVal(e.compareValue),
        }),
      );
    }
    if (e.muted) parts.push(i18n.t('performance:drillUi.gridCellThin'));
    return parts.join(' · ');
  };

  const title = i18n.t(
    grid.mode === 'dayparts'
      ? 'performance:drillUi.gridTitleDayparts'
      : 'performance:drillUi.gridTitle',
    { metric: short },
  );
  const meta = [
    i18n.t(
      grid.mode === 'dayparts'
        ? 'performance:drillUi.gridMetaDayparts'
        : 'performance:drillUi.gridMeta',
      { min: grid.minSample },
    ),
    grid.metricKey === 'turns' ? i18n.t('performance:drillUi.gridTurnsNote') : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const hue = goodHigh ? 'var(--petrol-ink)' : 'var(--st-alert)';
  const picked = new Set(selectedDows ?? []);

  return (
    <DrillTile
      sectionId="weekday-shift-grid"
      span={span}
      title={title}
      meta={meta}
      n={i18n.t('performance:drillUi.nReservations', {
        count: grid.total.n,
        n: fmt.num(grid.total.n),
      })}
    >
      <div className="ddrill-heat-wrap">
        <table className="ddrill-heat" style={{ '--ddrill-heat-hue': hue } as React.CSSProperties}>
          <thead>
            <tr>
              <th scope="col" className="ddrill-heat-corner">
                {i18n.t('performance:drillUi.gridCorner')}
              </th>
              {grid.columns.map(c => (
                <th
                  scope="col"
                  key={c.key}
                  title={c.isEvent ? `${c.label} · ${i18n.t('performance:drillUi.eventTag')}` : c.label}
                >
                  {c.label}
                  {c.isEvent ? (
                    <span className="ddrill-event-tag">{i18n.t('performance:drillUi.eventTag')}</span>
                  ) : null}
                </th>
              ))}
              <th scope="col" className="is-total">
                {i18n.t('performance:drillUi.gridAllShifts')}
              </th>
            </tr>
          </thead>
          <tbody>
            {grid.rows.map(r => {
              const total = rowTotal.get(r.dow);
              const on = picked.has(r.dow);
              return (
                <tr key={r.dow} className={on ? 'is-picked' : undefined}>
                  <th scope="row">
                    {onToggleDow ? (
                      <button
                        type="button"
                        className="ddrill-heat-day"
                        aria-pressed={on}
                        title={i18n.t('performance:drillUi.gridPickDay', { day: r.label })}
                        onClick={() => onToggleDow(r.dow)}
                      >
                        {r.label}
                      </button>
                    ) : (
                      r.label
                    )}
                  </th>
                  {grid.columns.map(c => {
                    const e = cellAt.get(`${r.dow}|${c.key}`);
                    if (!e) return <td key={c.key} />;
                    const empty = e.value == null;
                    // A rate gap inside normal variation keeps a faint tint, so
                    // only a real difference reads as hot.
                    const quiet = e.significant === false;
                    return (
                      <td
                        key={c.key}
                        className={`ddrill-heat-cell${e.muted ? ' is-muted' : ''}${empty ? ' is-empty' : ''}${peakKey === `${r.dow}|${c.key}` ? ' is-peak' : ''}`}
                        style={
                          e.muted || empty
                            ? undefined
                            : ({
                                '--ddrill-heat': heat(e.value) * (quiet ? 0.3 : 1),
                              } as React.CSSProperties)
                        }
                        title={cellTitle(r.label, c.label, e)}
                      >
                        <GridValue entry={e} fmtVal={fmtVal} goodHigh={goodHigh} />
                      </td>
                    );
                  })}
                  <td
                    className={`ddrill-heat-cell is-total${total?.muted ? ' is-muted' : ''}`}
                    title={total ? cellTitle(r.label, i18n.t('performance:drillUi.gridAllShifts'), total) : undefined}
                  >
                    {total ? <GridValue entry={total} fmtVal={fmtVal} goodHigh={goodHigh} /> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{i18n.t('performance:drillUi.gridAllDays')}</th>
              {grid.columns.map(c => {
                const e = colTotal.get(c.key);
                return (
                  <td
                    key={c.key}
                    className={`ddrill-heat-cell is-total${e?.muted ? ' is-muted' : ''}`}
                    title={e ? cellTitle(i18n.t('performance:drillUi.gridAllDays'), c.label, e) : undefined}
                  >
                    {e ? <GridValue entry={e} fmtVal={fmtVal} goodHigh={goodHigh} /> : null}
                  </td>
                );
              })}
              <td
                className="ddrill-heat-cell is-total is-grand"
                title={cellTitle(
                  i18n.t('performance:drillUi.gridAllDays'),
                  i18n.t('performance:drillUi.gridAllShifts'),
                  grid.total,
                )}
              >
                <GridValue entry={grid.total} fmtVal={fmtVal} goodHigh={goodHigh} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {peakCell && peakCell.value != null ? (
        <p className="ddrill-tile-note ddrill-heat-peak">
          {i18n.t('performance:drillUi.gridPeak', {
            day: dayLabel.get(peakCell.dow) ?? '',
            shift: colLabel.get(peakCell.col) ?? '',
            value: fmtVal(peakCell.value),
          })}
        </p>
      ) : null}
    </DrillTile>
  );
}
